/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { EuiConfirmModal, EuiContextMenuItem, EuiIcon, EuiTextColor } from '@elastic/eui'

import type { AnyAdminProject } from '@modules/project-admin-api/types'
import { getReprocessProjectMutationHook } from '@modules/project-admin-lib/hooks/reprocess'
import genericMessages from '@modules/project-lib/genericMessages'

export type Props = {
  project: AnyAdminProject
  onComplete: () => void
}

const ReprocessProjectButton: React.FunctionComponent<Props> = ({ project, onComplete }) => {
  const [isConfirming, setIsConfirming] = useState(false)

  const reprocessProjectMutation = getReprocessProjectMutationHook(project.type)()

  return (
    <Fragment>
      {renderButton()}
      {renderModal()}
    </Fragment>
  )

  function renderButton() {
    return (
      <EuiContextMenuItem
        key='delete'
        icon={<EuiIcon type='refresh' size='m' color='warning' />}
        onClick={openModal}
      >
        <EuiTextColor color='warning'>
          <FormattedMessage id='project.reconcileButton' defaultMessage='Reconcile project' />
        </EuiTextColor>
      </EuiContextMenuItem>
    )
  }

  function renderModal() {
    if (!isConfirming) {
      return
    }

    return (
      <EuiConfirmModal
        style={{ maxWidth: `40rem` }}
        buttonColor='warning'
        title={<FormattedMessage id='project.reconcile.title' defaultMessage='Are you sure?' />}
        onCancel={closeModal}
        onConfirm={performReprocess}
        isLoading={reprocessProjectMutation.isLoading}
        cancelButtonText={<FormattedMessage {...genericMessages.cancel} />}
        confirmButtonText={
          <FormattedMessage id='project.reconcile.reconcile' defaultMessage='Reconcile' />
        }
      >
        <p>
          <FormattedMessage
            id='project.reconcile.body'
            defaultMessage='This will trigger a reconciliation of the resources behind this project, the equivalent of applying a no-op plan to a deployment.'
          />
        </p>
      </EuiConfirmModal>
    )
  }

  function openModal() {
    setIsConfirming(true)
  }

  function closeModal() {
    setIsConfirming(false)
    onComplete()
  }

  function performReprocess() {
    reprocessProjectMutation.mutate({ id: project.id }, { onSuccess: closeModal })
  }
}

export default ReprocessProjectButton
